/**
 * Maximum length CAPS allows for the street NUMBER (APA_STREET_NUMBER).
 */
export const STREET_NUMBER_MAX_LENGTH = 10;

/**
 * Maximum length CAPS allows for the parsed street NAME (APA_STREET_NAME).
 */
export const STREET_NAME_MAX_LENGTH = 30;

/** Street number: digits, letters, whitespace, /, and -; must start with a digit. */
export const STREET_NUMBER_PATTERN = /^[0-9][0-9A-Za-z\s/-]*$/;

/** Street name: letters, numbers, spaces, /, and -. */
export const STREET_NAME_PATTERN = /^[A-Za-z0-9\s/-]+$/;

export type ParsedStreetAddress = {
  streetNumber: string;
  streetName: string;
};

/**
 * Splits an address line into street number and street name, mirroring how
 * CAPS stores them in separate columns. The split happens only on the first
 * whitespace run: text before it is the street number, text after it is the
 * street name. With no whitespace, the entire value is the street number.
 *
 * A line that does not start with a digit has no street number (the whole
 * line is treated as the street name). A number-only line yields an empty
 * street name (street name required is enforced by the contamination
 * validator). See SVRS-55574.
 */
export function parseStreetAddress(addressLine1: string): ParsedStreetAddress {
  const trimmed = addressLine1.trim();
  if (!trimmed) {
    return { streetNumber: '', streetName: '' };
  }

  // Lines that do not start with a digit have no street number
  // (e.g. "Main Street", "PO Box 17").
  if (!/^[0-9]/.test(trimmed)) {
    return { streetNumber: '', streetName: trimmed };
  }

  // Split on the first whitespace run only.
  const match = trimmed.match(/^(\S+)\s+(.+)$/);
  if (match) {
    return { streetNumber: match[1], streetName: match[2].trim() };
  }

  // No whitespace: the entire value is the street number.
  return { streetNumber: trimmed, streetName: '' };
}

/**
 * Returns the street NAME portion of an address line (number stripped).
 */
export const parseStreetName = (addressLine1: string): string =>
  parseStreetAddress(addressLine1).streetName;
